import { useEffect, useRef } from 'react'
import type { Product, ServiceChargeOverride } from '@/types'
import type { LineSelection } from '@/lib/lineModifiers'
import { foldCartCollisions, makeCartLine, type CartItem } from './cartLine'

/**
 * Local persistence for the in-progress NEW-order draft a cashier is building,
 * so a reload / crash / network blip doesn't vaporize a staged order.
 *
 * Stored in localStorage (the cart is small — we persist productId + qty + note,
 * never the Product object, which carries a multi-MB base64 image and would
 * freeze prices). Namespaced by the logged-in user so two cashiers sharing a
 * terminal never inherit each other's draft. This does NOT enable offline
 * selling — it only prevents data loss and powers the reconnect prompt.
 */

const DRAFT_KEY_PREFIX = 'pos-counter-draft-v1'
/** A held order older than one shift is almost always noise (and stale prices). */
export const DRAFT_MAX_AGE_MS = 12 * 60 * 60 * 1000

export interface PersistedDraftLine {
  productId: string
  quantity: number
  special_instructions?: string
  /**
   * Chosen modifier options (F-MOD-N2). Option ids and counts only — never a
   * price, never a label. A draft is re-resolved against the live menu on
   * restore, so anything cached here would only serve to resurrect a stale
   * figure. Omitted entirely when the line has no selections, which keeps
   * every pre-modifier draft byte-identical to what v1 wrote.
   */
  selections?: LineSelection[]
}

/**
 * Draft schema version.
 *
 * v1 -> v2 added `PersistedDraftLine.selections`. `readDraft` accepts BOTH: a
 * v1 draft restores with empty selections. Rejecting v1 outright would
 * silently vaporize every in-flight cart across the fleet the moment this
 * deploys, which is not an acceptable cost for a purely additive field.
 */
export type DraftVersion = 1 | 2
const DRAFT_VERSION: DraftVersion = 2

export interface PersistedDraft {
  version: DraftVersion
  userId: string
  savedAt: number
  orderType: string
  cart: PersistedDraftLine[]
  orderNotes: string
  selectedTableId: string | null
  customerName: string
  customerEmail: string
  customerPhone: string
  guestBirthday: string
  deliveryAddress: string
  deliveryFeeOverride: number | null
  /** Per-order service charge override on the un-placed ticket (optional: pre-081 drafts lack it). */
  serviceChargeOverride?: ServiceChargeOverride | null
}

/** Fields that make up a draft, minus the bookkeeping (`version`/`userId`/`savedAt`). */
export type DraftFields = Omit<PersistedDraft, 'version' | 'userId' | 'savedAt'>

function draftKey(userId: string): string {
  return `${DRAFT_KEY_PREFIX}:${userId}`
}

/** The logged-in user's id from `pos_user`, or null if unreadable. Never write an un-namespaced key. */
export function getDraftUserId(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem('pos_user')
    if (!raw) return null
    const u = JSON.parse(raw) as { id?: string }
    return typeof u.id === 'string' && u.id ? u.id : null
  } catch {
    return null
  }
}

/** A draft with no line items isn't worth persisting. */
function isEmptyDraft(fields: DraftFields): boolean {
  return fields.cart.length === 0
}

export function readDraft(userId: string): PersistedDraft | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(draftKey(userId))
    if (!raw) return null
    const d = JSON.parse(raw) as PersistedDraft
    // Accept v1 as well as v2 — see DraftVersion. A v1 line simply has no
    // `selections` key, and restoreDraftItems treats that as [].
    if ((d.version !== 1 && d.version !== 2) || d.userId !== userId || !Array.isArray(d.cart)) {
      return null
    }
    if (typeof d.savedAt !== 'number' || Date.now() - d.savedAt > DRAFT_MAX_AGE_MS) return null
    return d
  } catch {
    return null
  }
}

export function clearDraft(userId: string | null): void {
  if (typeof window === 'undefined' || !userId) return
  try {
    localStorage.removeItem(draftKey(userId))
  } catch {
    /* ignore */
  }
}

function writeDraft(userId: string, fields: DraftFields): void {
  if (typeof window === 'undefined') return
  try {
    if (isEmptyDraft(fields)) {
      localStorage.removeItem(draftKey(userId))
      return
    }
    const payload: PersistedDraft = {
      version: DRAFT_VERSION,
      userId,
      savedAt: Date.now(),
      ...fields,
    }
    localStorage.setItem(draftKey(userId), JSON.stringify(payload))
  } catch {
    /* localStorage full/blocked — non-fatal; the order still lives in React state */
  }
}

/**
 * Debounced autosave of the composed draft. `enabled` should be false whenever
 * the order is no longer a fresh draft (i.e. it became a server-side order, so
 * `continuingOrderId != null`) or there's no user to key on. Also flushes on
 * tab-hide, on unload, and on UNMOUNT.
 *
 * The unmount flush is the one that matters on a tablet (F-FE-N109). Switching
 * from the counter to the floor is a client-side route change: React unmounts
 * this screen and NEITHER `beforeunload` NOR `visibilitychange` fires, while the
 * debounce timer's own cleanup cancels the write that was already pending. So
 * everything the cashier did in the last half-second before switching screens
 * was dropped by the very mechanism meant to save it — the last item added is
 * exactly the half-second before you reach for the tab bar.
 *
 * `fields` should be a memoized object (keyed on its constituent state) so the
 * debounce effect only re-arms when the draft actually changes.
 */
export function useDraftAutosave(
  userId: string | null,
  fields: DraftFields,
  enabled: boolean,
): void {
  const fieldsRef = useRef(fields)
  fieldsRef.current = fields
  // Read at flush time rather than captured: `enabled` goes false the moment
  // the cart stops being a fresh draft, and the unmount flush must respect that
  // or it would write a placed order's cart back as an unplaced one.
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled

  // Debounced write on change.
  useEffect(() => {
    if (!enabled || !userId) return
    const t = window.setTimeout(() => writeDraft(userId, fieldsRef.current), 500)
    return () => window.clearTimeout(t)
  }, [enabled, userId, fields])

  // Flush immediately on hide / unload / unmount. Deliberately NOT keyed on
  // `enabled`: re-running this effect whenever the flag flips would fire the
  // cleanup — and therefore a write — at a moment that is not an unmount.
  useEffect(() => {
    if (!userId) return
    const flush = () => {
      if (enabledRef.current) writeDraft(userId, fieldsRef.current)
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    window.addEventListener('beforeunload', flush)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('beforeunload', flush)
      document.removeEventListener('visibilitychange', onVisibility)
      flush()
    }
  }, [userId])
}

/**
 * Reconcile persisted lines against the live menu. Unknown/unavailable products
 * are dropped; surviving lines are rebuilt from the FRESH product object, so
 * prices are always current (no stale prices are ever resurrected).
 *
 * Restored lines get FRESH `lineId`s — a line id is terminal-local render
 * identity, not something worth persisting — and their `mergeKey` is
 * recomputed from the reconciled content. Collisions are then folded, because
 * dropping an option from two otherwise-different lines can make them equal.
 *
 * `knownOptionIds`, when supplied, is the set of option ids the live menu still
 * offers; a selection outside it is dropped from the line and counted, so the
 * operator is told rather than silently charged the base price. Omit it and
 * selections pass through unchecked (used before the modifier catalogue loads).
 */
export function restoreDraftItems(
  lines: PersistedDraftLine[],
  liveProducts: Product[],
  knownOptionIds?: ReadonlySet<string>,
): { items: CartItem[]; dropped: number } {
  const byId = new Map(liveProducts.map((p) => [p.id, p]))
  const items: CartItem[] = []
  let dropped = 0
  for (const line of lines) {
    const product = byId.get(line.productId)
    if (!product || product.is_available === false) {
      dropped++
      continue
    }
    const persisted = line.selections ?? []
    const selections = knownOptionIds
      ? persisted.filter((s) => knownOptionIds.has(s.option_id))
      : persisted
    if (selections.length !== persisted.length) dropped++
    items.push(makeCartLine(product, line.quantity, selections, line.special_instructions))
  }
  return { items: foldCartCollisions(items), dropped }
}
